/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps, useFormik } from 'formik';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { connect } from 'react-redux';

import { Button, OutlinedInput, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../i18n';
import { SuperuserJiraConfigApiService } from '../../services/SuperuserJiraConfigApiService';
import { useFormStyles } from '../../styles/form';
import { ApiClientError } from '../../types/api/ApiClientError';
import { SUJiraConfig } from '../../types/superuser/config/SUJiraConfig';
import { fetchAdminJiraConfig, fetchJiraConfig } from '../../utils/queryFns';
import { reactToAdminUpdateJiraConfig } from '../../utils/reactions';
import { useApiValidation } from '../../utils/useApiValidation';
import { useValidationTranslation } from '../../utils/useValidationTranslation';
import { SystemConfigurationPlaceholder } from '../SystemConfigurationPage/SystemConfigurationPlaceholder';
import { generateInitialValues, supportConfigValidationSchema } from './SupportConfigurationPage.consts';
import { useSupportConfigurationPageStyles } from './SupportConfigurationPage.styles';
import { concatenateApiErrors } from './utils';

type FormProps = Omit<FormikProps<SUJiraConfig>, 'onSubmit'>;

const areInitialValuesNotEmpty = (values: ReturnType<typeof generateInitialValues>) => {
  return Object.values(values).every(value => value !== '');
};

const SupportConfigurationPageComponent = memo(() => {
  const { t } = useTranslation([Namespaces.Admin, Namespaces.Common]);
  const [configStatus, setConfigStatus] = useState('');
  const classes = useSupportConfigurationPageStyles();
  const [errorModal, setErrorModal] = useState('');

  const { setApiError } = useApiValidation();

  const queryConfig = useQuery(fetchAdminJiraConfig.key, fetchAdminJiraConfig.fn, {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const { data: configData, isLoading: isConfigLoading } = useQuery(fetchJiraConfig.key, fetchJiraConfig.fn, {
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const handleError = (error: ApiClientError) => {
    const validation = setApiError(error);
    if (validation.errorType === 'unexpected' || validation.errorType === 'common') {
      setErrorModal(t(`${Namespaces.Common}:errors.unexpected_error`));
    } else {
      const msg = concatenateApiErrors(validation);
      setErrorModal(msg);
    }
  };

  const [updateJiraConfig] = useMutation(SuperuserJiraConfigApiService.updateConfig, {
    onSuccess: async () => {
      await reactToAdminUpdateJiraConfig();
    },
    onError: (error: ApiClientError) => handleError(error),
  });

  const configPayload = useMemo(() => configData?.payload ?? { status: false }, [configData]);

  const handleTest = useCallback(() => {
    let message;
    if (configPayload.status) {
      message = t(`${Namespaces.Admin}:system_configuration.config_ok`);
    } else {
      message = t(`${Namespaces.Admin}:system_configuration.config_error`);

      if (configPayload.error) {
        message = `${message}: ${configPayload.error}`;
      }
    }
    setConfigStatus(message);
  }, [configPayload, t]);

  const handleCloseConfigModal = () => setConfigStatus('');

  const handleCloseErrorModal = () => setErrorModal('');

  const config = useMemo(() => queryConfig.data?.payload, [queryConfig]);

  const isLoading = useMemo(() => queryConfig.isLoading, [queryConfig.isLoading]);

  const handleSubmitData = async (values: SUJiraConfig) => await updateJiraConfig({ ...values });

  const formikProps = useFormik<SUJiraConfig>({
    initialValues: generateInitialValues(config),
    onSubmit: handleSubmitData,
    enableReinitialize: true,
    validationSchema: supportConfigValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
  });

  return (
    <>
      <Box maxWidth={620}>
        {isLoading ? (
          <SystemConfigurationPlaceholder inputs={2} />
        ) : (
          <>
            <Typography variant="h2" className={classes.title}>
              {t('system_configuration.support.title')}
            </Typography>
            <form onSubmit={formikProps.handleSubmit}>
              <SupportForm {...(formikProps as FormProps)} />
              <Box mt={2} display="flex" justifyContent="space-between">
                <Button
                  variant="contained"
                  disabled={formikProps.dirty || !areInitialValuesNotEmpty(formikProps.initialValues)}
                  onClick={handleTest}
                  type="button"
                >
                  {t('buttons.test-connection')}
                </Button>
                <Button type="submit" variant="contained" disabled={!formikProps.dirty || queryConfig.isLoading}>
                  {t('buttons.submit')}
                </Button>
              </Box>
            </form>
          </>
        )}
      </Box>
      <SimpleNotifyModal
        open={!!configStatus && !isConfigLoading}
        message={configStatus}
        onOk={handleCloseConfigModal}
        okButtonText={t('buttons.ok')}
      />
      <SimpleNotifyModal
        open={!!errorModal.length}
        message={errorModal}
        onOk={handleCloseErrorModal}
        okButtonText={t('buttons.ok')}
      />
    </>
  );
});

const SupportForm = memo<FormProps>(props => {
  const { touched, values, errors, handleChange, handleBlur } = props;
  const formClasses = useFormStyles();
  const { t } = useTranslation(Namespaces.Admin);
  const { vtr } = useValidationTranslation();
  const { hasFieldApiError, getFieldApiError } = useApiValidation();

  const classes = useSupportConfigurationPageStyles();

  const hasError = (name: keyof SUJiraConfig): boolean => {
    return !!touched[name] && (!!errors[name] || hasFieldApiError(name));
  };

  const getError = (name: keyof SUJiraConfig): string => {
    const error = errors[name] || getFieldApiError(name);
    return error ? error : '';
  };

  return (
    <>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasError('atlassianUrl')}
        variant="outlined"
        required
      >
        <FormHelperText error={false}>{t('system_configuration.support.form.atlassianUrlExplanation')}</FormHelperText>

        <Tooltip title={values.atlassianUrl} enterDelay={300} enterNextDelay={300} arrow>
          <OutlinedInput
            name="atlassianUrl"
            className={classes.input}
            value={values.atlassianUrl}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={t('system_configuration.support.form.atlassianUrlPlaceholder')}
          />
        </Tooltip>

        {hasError('atlassianUrl') && (
          <FormHelperText>
            {vtr(getError('atlassianUrl'), {
              field: t('system_configuration.support.form.atlassianUrl'),
            })}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasError('email')}
        variant="outlined"
        required
      >
        <FormHelperText error={false}>{t('system_configuration.support.form.emailExplanation')}</FormHelperText>

        <Tooltip title={values.email} enterDelay={300} enterNextDelay={300} arrow>
          <OutlinedInput
            name="email"
            className={classes.input}
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={t('system_configuration.support.form.emailPlaceholder')}
          />
        </Tooltip>

        {hasError('email') && (
          <FormHelperText>
            {vtr(getError('email'), {
              field: t('system_configuration.support.form.email'),
            })}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasError('token')}
        variant="outlined"
        required
      >
        <FormHelperText error={false}>
          {t('system_configuration.support.form.tokenPart1')}
          <a href={t('system_configuration.support.form.tokenLink')} target="_blank" rel="noopener noreferrer">
            {t('system_configuration.support.form.tokenPart2')}
          </a>
          {t('system_configuration.support.form.tokenPart3')}
        </FormHelperText>

        <Tooltip title={values.token} enterDelay={300} enterNextDelay={300} arrow>
          <OutlinedInput
            name="token"
            className={classes.input}
            value={values.token}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={t('system_configuration.support.form.tokenPlaceholder')}
          />
        </Tooltip>

        {hasError('token') && (
          <FormHelperText>
            {vtr(getError('token'), {
              field: t('system_configuration.support.form.token'),
            })}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasError('serviceDeskKey')}
        variant="outlined"
        required
      >
        <FormHelperText error={false}>
          {t('system_configuration.support.form.serviceDeskKeyExplanation')}
        </FormHelperText>

        <Tooltip title={values.serviceDeskKey} enterDelay={300} enterNextDelay={300} arrow>
          <OutlinedInput
            name="serviceDeskKey"
            className={classes.input}
            value={values.serviceDeskKey}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={t('system_configuration.support.form.serviceDeskKeyPlaceholder')}
          />
        </Tooltip>

        {hasError('serviceDeskKey') && (
          <FormHelperText>
            {vtr(getError('serviceDeskKey'), {
              field: t('system_configuration.support.form.serviceDeskKey'),
            })}
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
});

export const SupportConfigurationPage = connect()(SupportConfigurationPageComponent);
